import React, { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { isPublicEntryPath } from '../utils/publicRoutes';
import { ADMIN_ROLES } from '../constants/roles';

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
  requiredPermission?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback = <div>Please log in to access this page.</div>,
  requiredPermission,
}) => {
  const { isAuthenticated, isLoading, user, hasPermission } = useAuth();
  const navigate = useNavigate();

  // AuthGuard (inside App) owns the login redirect. Calling login here too
  // races AuthGuard and cancels in-flight Auth0 authorize requests.
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      const { pathname } = window.location;
      const currentPath = pathname + window.location.search;
      if (!isPublicEntryPath(pathname)) {
        sessionStorage.setItem('intendedDestination', currentPath);
      }
    }
  }, [isAuthenticated, isLoading]);

  // Handle role-based redirects for authenticated users
  useEffect(() => {
    if (isAuthenticated && user && !isLoading) {
      const hasAdminRole = ADMIN_ROLES.some((r) => user.roles?.includes(r));
      if (!hasAdminRole) {
        navigate('/access-denied', { replace: true });
        return;
      }
      if (requiredPermission && !hasPermission(requiredPermission)) {
        navigate('/insufficient-permissions', { replace: true });
      }
    }
  }, [isAuthenticated, user, isLoading, navigate, requiredPermission, hasPermission]);

  if (user) {
    const hasAdminRole = ADMIN_ROLES.some((r) => user.roles?.includes(r));
    if (!hasAdminRole) {
      return <div>Access denied. Admin privileges required.</div>;
    }
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return <div>Access denied. Insufficient permissions.</div>;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
